/**
 * useMetaData Hook
 * Fetches Meta Pixel insights data (events and purchases)
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuthToken } from './useAuthToken';

const useMetaData = (days = 30) => {
  const [events, setEvents] = useState([]);
  const [purchases, setPurchases] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasAccessToken, setHasAccessToken] = useState(false);
  const { token } = useAuthToken();

  const fetchData = async () => {
    if (!token) return;

    const businessId = localStorage.getItem('user_id');
    if (!businessId) {
      setError('Business ID not found');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Fetch events and purchases in parallel
      const [eventsRes, purchasesRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_APIURL}meta/insights/events`, {
          params: { business_id: businessId, days },
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${process.env.REACT_APP_APIURL}meta/insights/purchases`, {
          params: { business_id: businessId, days },
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      // Process events response
      if (eventsRes.data.success) {
        setEvents(eventsRes.data.data.events || []);
        setHasAccessToken(eventsRes.data.data.hasAccessToken || false);
      }

      // Process purchases response
      if (purchasesRes.data.success) {
        setPurchases(purchasesRes.data.data);
      }

      setIsLoading(false);
    } catch (err) {
      console.error('[Meta] Data fetch error:', err);
      setError(err.response?.data?.message || 'Failed to fetch Meta Pixel data');
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token, days]);

  return {
    events,
    purchases,
    isLoading,
    error,
    hasAccessToken,
    refetch: fetchData
  };
};

export default useMetaData;
